import {
  EffectOnTargetStatus,
  type StudyMissionSelectDto,
  type StudySelectDto,
  type StudyTargetEffectOverrideSelectDto,
  type StudyThreadSelectDto,
} from '@@types/api.types';
import { Modal } from '@components/core/modal.component';
import { TooltipComponent } from '@components/core/tooltip.component';
import { EmptyStateContainer } from '@components/shared/empty-state-container.component';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Label } from '@components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select';
import { Skeleton } from '@components/ui/skeleton';
import { Textarea } from '@components/ui/textarea';
import { errorToast, successToast } from '@hooks/toast.hook';
import {
  useClearMeiaTargetEffectOverride,
  useGetMeiaStudySwimlaneTasks,
  useGetMeiaStudyTargetEngagements,
  useGetMeiaStudyThreadSwimlanes,
  useGetMeiaTargetEffectOverrides,
  useGetMeiaThreatTargetSystems,
  useUpsertMeiaTargetEffectOverride,
} from '@queries/meia.query';
import { ExternalLinkIcon, InfoIcon } from 'lucide-react';
import { type FC, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { meiaRoute } from '../meia.constants';
import { getMeiaMissionTitle } from './meia-mission-display.util';
import { ALL_MISSIONS_FILTER_VALUE, MeiaMissionFilterPills } from './meia-mission-filter-pills.component';
import {
  type MeiaMissionTargetEffectRow,
  buildEngagementMissionIndex,
  buildMissionTargetEffectRows,
  formatOverrideAuthorName,
  formatPkEstimate,
  getEffectOnTargetStatusBadgeVariant,
  getEffectOnTargetStatusDescription,
  getEffectOnTargetStatusLabel,
} from './meia-p3-engagement.util';
import { formatRunCaseDate } from './meia-p4-run-cases.util';

type MeiaP3TargetEffectOverridesProps = {
  missions: StudyMissionSelectDto[];
  study: StudySelectDto;
  threads: StudyThreadSelectDto[];
};

type OverrideModalState =
  | { mode: 'closed' }
  | {
      mode: 'editing';
      rationale: string;
      row: MeiaMissionTargetEffectRow;
      status: EffectOnTargetStatus;
    };

const effectOnTargetStatusOptions = Object.values(EffectOnTargetStatus).map((status) => ({
  label: getEffectOnTargetStatusLabel(status),
  value: status,
}));

const effectOnTargetStatusValues = new Set<string>(Object.values(EffectOnTargetStatus));

const isEffectOnTargetStatus = (value: string): value is EffectOnTargetStatus => effectOnTargetStatusValues.has(value);

const getOverrideKey = (override: StudyTargetEffectOverrideSelectDto): string =>
  `${override.architectureAlternativeId ?? 'study'}:${override.threatTargetSystemCode}`;

export const MeiaP3TargetEffectOverrides: FC<MeiaP3TargetEffectOverridesProps> = (props) => {
  const { missions, study, threads } = props;
  const studyId = study.id;
  const [modal, setModal] = useState<OverrideModalState>({ mode: 'closed' });
  const [selectedMissionFilter, setSelectedMissionFilter] = useState(ALL_MISSIONS_FILTER_VALUE);
  // Per-alternative Effect-on-Target scoring is deferred (AZ-452); the alternative picker is hidden
  // and overrides are authored/read at the study level. The override store stays alternative-keyed.
  const selectedArchitectureAlternativeId = null;

  const { data: targetEffectOverrides = [], isLoading: isLoadingOverrides } = useGetMeiaTargetEffectOverrides({
    studyId,
  });
  const { data: threatTargetSystems = [], isLoading: isLoadingTargets } = useGetMeiaThreatTargetSystems();
  const { swimlanes } = useGetMeiaStudyThreadSwimlanes({ studyId, threads });
  const { tasks } = useGetMeiaStudySwimlaneTasks({ studyId, swimlanes });
  const { data: engagements = [], isLoading: isLoadingEngagements } = useGetMeiaStudyTargetEngagements({ studyId });
  const { isPending: isClearing, mutateAsync: clearOverride } = useClearMeiaTargetEffectOverride();
  const { isPending: isUpserting, mutateAsync: upsertOverride } = useUpsertMeiaTargetEffectOverride();

  const missionIdByTaskId = useMemo(
    () => buildEngagementMissionIndex({ swimlanes, tasks, threads }),
    [swimlanes, tasks, threads],
  );
  const rows = useMemo(
    () =>
      buildMissionTargetEffectRows({
        architectureAlternativeId: selectedArchitectureAlternativeId,
        engagements,
        missionIdByTaskId,
        overrides: targetEffectOverrides,
        targetSystems: threatTargetSystems,
      }),
    [engagements, missionIdByTaskId, targetEffectOverrides, threatTargetSystems],
  );
  const missionById = useMemo(() => new Map(missions.map((mission) => [mission.id, mission])), [missions]);
  const rowCountByMissionId = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of rows) {
      if (!row.missionId) continue;
      counts.set(row.missionId, (counts.get(row.missionId) ?? 0) + 1);
    }
    return counts;
  }, [rows]);
  const visibleRows = useMemo(
    () =>
      selectedMissionFilter === ALL_MISSIONS_FILTER_VALUE
        ? rows
        : rows.filter((row) => row.missionId === selectedMissionFilter),
    [rows, selectedMissionFilter],
  );
  const overriddenCount = visibleRows.filter((row) => row.decisionMakerOverride).length;
  const canManageOverrides = study.canManageOverrides;
  const isLoading = isLoadingEngagements || isLoadingOverrides || isLoadingTargets;
  const overrideByKey = useMemo(
    () => new Map(targetEffectOverrides.map((override) => [getOverrideKey(override), override])),
    [targetEffectOverrides],
  );

  const handleOpenOverride = (row: MeiaMissionTargetEffectRow): void => {
    setModal({
      mode: 'editing',
      rationale: row.decisionMakerOverride?.rationale ?? '',
      row,
      status: row.decisionMakerOverride?.status ?? row.modelStatus,
    });
  };

  const handleSaveOverride = async (): Promise<void> => {
    if (modal.mode === 'closed') return;
    const rationale = modal.rationale.trim();
    if (!rationale) {
      errorToast({ description: 'Add override rationale.' });
      return;
    }

    await upsertOverride({
      architectureAlternativeId: selectedArchitectureAlternativeId,
      rationale,
      status: modal.status,
      studyId,
      threatTargetSystemCode: modal.row.threatTargetSystemCode,
    });
    successToast({ description: 'Target effect override saved.' });
    setModal({ mode: 'closed' });
  };

  const handleClearOverride = async (row: MeiaMissionTargetEffectRow): Promise<void> => {
    await clearOverride({
      architectureAlternativeId: selectedArchitectureAlternativeId,
      studyId,
      threatTargetSystemCode: row.threatTargetSystemCode,
    });
    successToast({ description: 'Target effect override cleared.' });
  };

  return (
    <>
      <section className="flex flex-col gap-4 rounded-xl bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 max-w-2xl">
            <h3 className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-900">
              Effect on Target · overrides
              <TooltipComponent>
                The model computes a status for each target from its authored task engagements. Use this view to pin a
                different outcome when mission context or authoritative judgment warrants it — with a rationale
                attached. Overrides apply only to the selected alternative.
              </TooltipComponent>
            </h3>
            <p className="mt-1 text-xs leading-relaxed text-gray-500">
              The model computes a status for each target from the authored engagements. Pin a different outcome when
              authoritative judgment warrants it — overrides apply only to the selected alternative.
            </p>
          </div>
          <Button
            asChild
            size="xs"
            variant="outline"
          >
            <Link to={meiaRoute.studyTab({ studyId, tab: 'p5' })}>
              <ExternalLinkIcon className="size-3" />
              Open full EOT view
            </Link>
          </Button>
        </div>

        {/* The architecture-alternative picker is hidden — per-alternative EOT scoring is deferred (AZ-452);
            overrides are authored study-level. */}
        <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 pb-3">
          <MeiaMissionFilterPills
            countByMissionId={rowCountByMissionId}
            missions={missions}
            onSelect={setSelectedMissionFilter}
            selectedMissionId={selectedMissionFilter}
            totalCount={rows.length}
          />

          <div className="ml-auto flex items-center gap-2 text-xs text-gray-500">
            <span>
              <span className="font-medium text-gray-800">{visibleRows.length}</span>{' '}
              {visibleRows.length === 1 ? 'target' : 'targets'}
            </span>
            <span className="text-gray-300">·</span>
            <span>
              <span className="font-medium text-gray-800">{overriddenCount}</span> overridden
            </span>
            <span className="text-gray-300">·</span>
            <span>
              <span className="font-medium text-gray-800">{visibleRows.length - overriddenCount}</span> matching model
            </span>
          </div>
        </div>

        {isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : visibleRows.length === 0 ? (
          <EmptyStateContainer
            className="min-h-[280px] rounded-md border border-dashed border-gray-300"
            message="Open a task in the Task Canvas and add target engagements to its technologies — they feed this view."
            title="No Target Engagements"
          />
        ) : (
          <div className="overflow-x-auto rounded-md border border-gray-200">
            <table className="w-full min-w-[860px] text-left text-xs">
              <thead className="bg-gray-50 text-[11px] font-semibold uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2">Target</th>
                  <th className="px-3 py-2">Mission</th>
                  <th className="px-3 py-2">
                    <span className="inline-flex items-center gap-1">
                      Model says
                      <TooltipComponent>
                        Status the model computed for this target from the authored engagements of the selected
                        alternative.
                      </TooltipComponent>
                    </span>
                  </th>
                  <th className="px-3 py-2">
                    <span className="inline-flex items-center gap-1">
                      Decision maker says
                      <TooltipComponent>
                        Decision-maker override for this target, when one is pinned. Blank rows follow the model.
                      </TooltipComponent>
                    </span>
                  </th>
                  <th className="px-3 py-2">Rationale</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {visibleRows.map((row) => {
                  const override =
                    row.decisionMakerOverride ??
                    overrideByKey.get(`${selectedArchitectureAlternativeId ?? 'study'}:${row.threatTargetSystemCode}`);
                  const mission = row.missionId ? missionById.get(row.missionId) : undefined;

                  return (
                    <tr
                      key={`${row.architectureAlternativeId ?? 'study'}:${row.missionId}:${row.threatTargetSystemCode}`}
                    >
                      <td className="px-3 py-3 align-top">
                        <div className="flex flex-col gap-1">
                          <span className="font-medium text-gray-900">{row.targetName}</span>
                          <span className="text-gray-500">{row.threatTargetSystemCode}</span>
                          <span className="text-gray-400">
                            {row.engagementCount} {row.engagementCount === 1 ? 'engagement' : 'engagements'} ·{' '}
                            {formatPkEstimate(row.maxPkEstimate)}
                          </span>
                        </div>
                      </td>
                      <td className="max-w-44 px-3 py-3 align-top text-gray-600">
                        {mission ? <span className="line-clamp-2">{getMeiaMissionTitle(mission)}</span> : '—'}
                      </td>
                      <td className="px-3 py-3 align-top">
                        <TooltipComponent
                          trigger={
                            <span className="inline-flex cursor-help">
                              <Badge
                                size="xs"
                                variant={getEffectOnTargetStatusBadgeVariant(row.modelStatus)}
                              >
                                {getEffectOnTargetStatusLabel(row.modelStatus)}
                              </Badge>
                            </span>
                          }
                        >
                          {getEffectOnTargetStatusDescription(row.modelStatus)}
                        </TooltipComponent>
                      </td>
                      <td className="px-3 py-3 align-top">
                        {override ? (
                          <div className="flex flex-col gap-1">
                            <TooltipComponent
                              trigger={
                                <span className="inline-flex cursor-help">
                                  <Badge
                                    size="xs"
                                    variant={getEffectOnTargetStatusBadgeVariant(override.status)}
                                  >
                                    {getEffectOnTargetStatusLabel(override.status)}
                                  </Badge>
                                </span>
                              }
                            >
                              {getEffectOnTargetStatusDescription(override.status)}
                            </TooltipComponent>
                            <span className="text-[11px] text-gray-400">
                              — {formatOverrideAuthorName(override)} · {formatRunCaseDate(override.updatedAt)}
                            </span>
                          </div>
                        ) : (
                          <span className="italic text-gray-400">matches model</span>
                        )}
                      </td>
                      <td className="max-w-80 px-3 py-3 align-top text-gray-600">
                        {override?.rationale ?? <span className="text-gray-400">-</span>}
                      </td>
                      <td className="px-3 py-3 text-right align-top">
                        {canManageOverrides ? (
                          <div className="flex justify-end gap-2">
                            <Button
                              onClick={() => handleOpenOverride(row)}
                              size="xs"
                              type="button"
                              variant="secondary"
                            >
                              {override ? 'Edit' : 'Override'}
                            </Button>
                            {override && (
                              <Button
                                disabled={isClearing}
                                onClick={() => void handleClearOverride(row)}
                                size="xs"
                                type="button"
                                variant="destructive"
                              >
                                Clear
                              </Button>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">Read-only</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <p className="flex items-center gap-1.5 text-xs text-gray-500">
          <InfoIcon className="size-3 shrink-0" />
          Overrides require decision-maker authority and are recorded with the study. They do not block the Phase 3
          gate.
        </p>
      </section>

      <Modal
        centered
        header={{ title: 'Decision-maker override' }}
        isOpen={modal.mode !== 'closed'}
        onClose={() => setModal({ mode: 'closed' })}
        width="560px"
      >
        {modal.mode !== 'closed' && (
          <div className="flex flex-col gap-4">
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
              <span className="font-medium text-gray-900">{modal.row.targetName}</span>
              <span className="ml-2">{modal.row.threatTargetSystemCode}</span>
            </div>

            <div className="flex flex-col gap-2">
              <Label>Status</Label>
              <Select
                onValueChange={(value) => {
                  if (isEffectOnTargetStatus(value)) setModal({ ...modal, status: value });
                }}
                value={modal.status}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {effectOnTargetStatusOptions.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-2">
              <Label>Rationale</Label>
              <Textarea
                onChange={(event) => setModal({ ...modal, rationale: event.target.value })}
                placeholder="Why the model's answer is wrong for this target — mission context, intel, doctrinal judgment…"
                rows={4}
                value={modal.rationale}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                disabled={isUpserting}
                onClick={() => setModal({ mode: 'closed' })}
                type="button"
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                loading={isUpserting}
                onClick={() => void handleSaveOverride()}
                type="button"
              >
                Save override
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};
